/// <reference types="jquery" />
class Wishlist {
    private storageKey = 'es_wishlist';
    private buttonSelector = '.wishlist-icon';

    constructor() {
        // Load wishlist from URL on page load
        this.loadFromUrl();

        // Highlight items already in wishlist on page load
        this.updateButtonStates();

        // Bind click events
        this.bindEvents();

        // Update cart count in header
        this.updateWishListCount();

        // Update wishlist URL in header
        this.wishlistUrl();
    }

    /** Load wishlist IDs from URL on page load */
    private loadFromUrl(): void {
        const pathMatch = window.location.pathname.match(/\/wishlist\/batchid\/([^\/]+)\/?/);
        
        if (pathMatch && pathMatch[1]) {
            const encodedIds = pathMatch[1];
            const decodedIds = decodeURIComponent(encodedIds);
            
            const ids = decodedIds.split(',')
                .map(id => parseInt(id.trim(), 10))
                .filter(id => !isNaN(id) && id > 0);
            
            if (ids.length > 0) {
                this.saveList(ids);
            }
        }
    }

    // Update the wishlist link in the header
    private wishlistUrl(): void {

        // Find the wishlist link in the header
        const wishlistLink = document.querySelector('.header-account-bar .wishlist-btn');

        // Get current wishlist IDs
        const wishlistIds = this.getList();

        // Create a comma-separated string of IDs
        const idString = wishlistIds.join(',');

        // Encode the IDs for URL
        const encodedIds = encodeURIComponent(idString);

        // Update the href attribute of the wishlist link
        if (wishlistLink) {
            if (wishlistIds.length > 0) {
                wishlistLink.setAttribute('href', `/wishlist/batchid/${encodedIds}/`);
            } else {
                wishlistLink.setAttribute('href', `/wishlist/`);
            }
        }

        // Update current page URL if on wishlist page
        if (window.location.pathname.includes('/wishlist/')) {
            // If no items left, redirect to base wishlist page
            if (wishlistIds.length === 0) {
                const baseUrl = '/wishlist/';
                if (window.history && window.history.pushState) {
                    window.history.pushState({}, '', baseUrl);
                }
            } else {
                const newUrl = `/wishlist/batchid/${encodedIds}/`;
                if (window.history && window.history.pushState) {
                    window.history.pushState({}, '', newUrl);
                }
            }
        }
    }

    /** Get current list of IDs from localStorage */
    private getList(): number[] {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return [];
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed.map(n => Number(n)).filter(n => !isNaN(n));
            }
        } catch { }
        return [];
    }

    /** Save updated list to localStorage */
    private saveList(list: number[]): void {
        localStorage.setItem(this.storageKey, JSON.stringify(list));
        this.updateWishListCount();
        this.wishlistUrl()
    }

    /** Toggle a post ID in the wishlist and show messages */
    private toggleItem(id: number): void {
        console.log(id);
        const list = this.getList();
        const index = list.indexOf(id);
        let action: "added" | "removed";

        if (index >= 0) {
            // Remove
            list.splice(index, 1);
            action = "removed";
        } else {
            // Add
            list.push(id);
            action = "added";
        }

        this.saveList(list);
        this.updateButtonStates();
        this.updateWishListCount();
        this.wishlistUrl()
        this.showMessage(action);
    }

    /** Add click listeners to buttons */
    private bindEvents(): void {

        document.addEventListener('click', (e: Event) => {
            
            const button = (e.target as HTMLElement).closest(this.buttonSelector) as HTMLElement | null;
            if (!button) return;

            // Try to get data-id from parent <li>
            let dataId: string | null = button.closest('li')?.getAttribute('data-id') ?? null;

            // If no <li> found, check if the button itself has data-id
            // or a parent container such as .sample-wish-buttons or a .product-list > li
            if (!dataId) {

                // Try to find data-id from related elements
                const buttonDataId: string | null = button.getAttribute('data-id');
                const sampleButtonsId: string | null = button
                    .closest<HTMLElement>('.sample-wish-buttons')
                    ?.getAttribute('data-id') ?? null;

                const listItem: HTMLElement | null = button.closest('.product-list > li');
                const listItemId: string | null = listItem?.getAttribute('data-id') ?? null;

                if (buttonDataId) {

                    dataId = buttonDataId;

                } else if (sampleButtonsId) {

                    dataId = sampleButtonsId;

                } else if (listItemId) {

                    dataId = listItemId;

                    // Toggle "selected" safely if list item exists
                    listItem?.classList.toggle('selected');

                } else {

                    // Fallback if none found
                    dataId = null;

                }
            }

            if (!dataId) return;

            const id = parseInt(dataId, 10);
            if (!id) return;

            this.toggleItem(id);

        });
    }

    /** Update all buttons on the page based on current wishlist */
    private updateButtonStates(): void {

        // Get the current list of wishlist item IDs from localStorage
        const list = this.getList();

        // Select all wishlist buttons on the page
        document.querySelectorAll<HTMLElement>(this.buttonSelector).forEach(button => {

            // Try to get data-id from multiple sources (same logic as bindEvents)
            let dataId: string | null = button.closest('li')?.getAttribute('data-id') ?? null;

            // If no <li> found, check other containers
            if (!dataId) {
                const sampleButtonsId: string | null = button
                    .closest<HTMLElement>('.sample-wish-buttons')
                    ?.getAttribute('data-id') ?? null;

                const listItem: HTMLElement | null = button.closest('.product-list > li');
                const listItemId: string | null = listItem?.getAttribute('data-id') ?? null;

                if (sampleButtonsId) {
                    dataId = sampleButtonsId;
                } else if (listItemId) {
                    dataId = listItemId;
                }
            }

            if (!dataId) return; // Skip if no data-id found

            // Parse the ID into an integer
            const id = parseInt(dataId, 10);
            if (!id) return; // Skip if invalid

            // Check whether the current item ID exists in the stored wishlist
            const inList = list.includes(id);

            // Toggle the 'in-wishlist' class on the button based on presence in the list
            button.classList.toggle('in-wishlist', inList);

            // Update the button's label text if a .wishlist-text element exists inside it
            const label = button.querySelector('.wishlist-text');
            if (label) {
                label.textContent = inList ? 'Remove from Wishlist' : 'Add to Wishlist';
            }
        });
    }

    /** Update the wishlist count displayed in the header */
    updateWishListCount(): void {
        const count = this.getList().length;
        const countElement = document.querySelector<HTMLElement>('.header-account-bar .wishlist-count');

        if (countElement) {
            countElement.textContent = count > 0 ? count.toString() : '';
        }
    }

    /** Show a temporary message when an item is added or removed from the wishlist */
    private showMessage(action: "added" | "removed"): void {
        const message = action === "added" ? "Added to Wishlist" : "Removed from Wishlist";
        const messageElement = document.createElement('div');
        messageElement.className = 'wishlist-message';
        messageElement.textContent = message;

        document.body.appendChild(messageElement);

        setTimeout(() => {
            messageElement.remove();
        }, 2000);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new Wishlist();
});